/**
 * Compact summary of selected grade and state filters for performance reports.
 * One pill per filter value, styled like PerfSettings (grade = orange, state = green) but smaller.
 * Optional Training/Racing and tack (PORT/STBD) pills when selected.
 */

import { For, Show, createSignal } from "solid-js";
import { Portal } from "solid-js/web";
import {
  maneuverStateFilterPillValues,
  parseManeuverStateFilterString,
} from "../../utils/maneuverFoilingState";

interface PerformanceFilterSummaryProps {
  filterGrades: string;
  filterState: string;
  /** When set (maneuver pages), show ALL when every available foiling state is selected. */
  stateOptions?: string[];
  /** When 'TRAINING' or 'RACING', show a pill; otherwise don't show. */
  trainingRacing?: "TRAINING" | "RACING" | null;
  /** When 'PORT' or 'STBD', show tack entry filter pill. */
  tackEntry?: "PORT" | "STBD" | null;
  /** When 'PORT' or 'STBD', show tack filter pill (performance pages - tack, not maneuver entry). */
  tack?: "PORT" | "STBD" | null;
  /** When true (e.g. maneuver page), format grade pills as ">1", ">2", etc. */
  gradeAsGreaterThan?: boolean;
}

function parseGradeValues(grades: string, prefixGreaterThan: boolean): string[] {
  const s = (grades || "").trim();
  if (!s) return prefixGreaterThan ? ["ALL"] : ["All grades"];
  const list = s.split(",").map((g) => g.trim()).filter(Boolean);
  if (list.length === 0) return prefixGreaterThan ? ["ALL"] : ["All grades"];
  if (prefixGreaterThan) {
    const nums = list.map((g) => Number(g)).filter((n) => !isNaN(n));
    if (nums.length === 1 && nums[0] === -1) return ["ALL"];
    return list.map((g) => {
      const n = Number(g);
      if (!isNaN(n) && n === -1) return "ALL";
      return `>${g}`;
    });
  }
  return list;
}

function parseStateValues(state: string): string[] {
  const s = (state || "").trim();
  if (!s) return ["All"];
  const list = s.split(",").map((x) => x.trim().toUpperCase()).filter(Boolean);
  return list.length > 0 ? list : ["All"];
}

interface StatesTooltipPillProps {
  extraStates: string[];
}

function StatesTooltipPill(props: StatesTooltipPillProps) {
  const [visible, setVisible] = createSignal(false);
  const [pos, setPos] = createSignal({ x: 0, y: 0 });

  const handleMouseEnter = (e: MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
    setVisible(true);
  };
  const handleMouseMove = (e: MouseEvent) => {
    setPos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseLeave = () => {
    setVisible(false);
  };

  return (
    <>
      <span
        class="performance-filter-summary__pill performance-filter-summary__pill--state performance-filter-summary__pill--overflow"
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        aria-label={`+${props.extraStates.length} more states: ${props.extraStates.join(", ")}`}
      >
        +{props.extraStates.length}
      </span>
      <Show when={visible()}>
        <Portal mount={document.body}>
          <div
            class="performance-filter-summary__overflow-tooltip"
            style={{ left: `${pos().x + 10}px`, top: `${pos().y - 8}px` }}
          >
            <For each={props.extraStates}>
              {(s) => (
                <span class="performance-filter-summary__pill performance-filter-summary__pill--state">
                  {s}
                </span>
              )}
            </For>
          </div>
        </Portal>
      </Show>
    </>
  );
}

export default function PerformanceFilterSummary(props: PerformanceFilterSummaryProps) {
  const gradeValues = () => parseGradeValues(props.filterGrades, !!props.gradeAsGreaterThan);
  const stateValues = () => {
    if (props.stateOptions) {
      return maneuverStateFilterPillValues(
        parseManeuverStateFilterString(props.filterState),
        props.stateOptions
      );
    }
    return parseStateValues(props.filterState);
  };

  return (
    <div class="performance-filter-summary" aria-label="Active filters">
      <span class="performance-filter-summary__title">Active Filters</span>
      <div class="performance-filter-summary__row performance-filter-summary__row--inline">
        <div class="performance-filter-summary__group">
          <span class="performance-filter-summary__row-label">Grade:</span>
          <div class="performance-filter-summary__pills">
            <For each={gradeValues()}>
              {(value) => <span class="performance-filter-summary__pill performance-filter-summary__pill--grade">{value}</span>}
            </For>
          </div>
        </div>
        <div class="performance-filter-summary__group">
          <span class="performance-filter-summary__row-label">State:</span>
          <div class="performance-filter-summary__pills">
            <Show
              when={stateValues().length > 1}
              fallback={
                <For each={stateValues()}>
                  {(value) => (
                    <span class="performance-filter-summary__pill performance-filter-summary__pill--state">
                      {value}
                    </span>
                  )}
                </For>
              }
            >
              <span class="performance-filter-summary__pill performance-filter-summary__pill--state">
                {stateValues()[0]}
              </span>
              <StatesTooltipPill extraStates={stateValues().slice(1)} />
            </Show>
          </div>
        </div>
        <Show when={props.trainingRacing === "TRAINING" || props.trainingRacing === "RACING"}>
          <div class="performance-filter-summary__group">
            <span class="performance-filter-summary__row-label">Type:</span>
            <div class="performance-filter-summary__pills">
              <span class="performance-filter-summary__pill performance-filter-summary__pill--trainingracing">{props.trainingRacing}</span>
            </div>
          </div>
        </Show>
        <Show when={props.tack === "PORT" || props.tack === "STBD"}>
          <div class="performance-filter-summary__group">
            <span class="performance-filter-summary__row-label">Tack:</span>
            <div class="performance-filter-summary__pills">
              <span class="performance-filter-summary__pill performance-filter-summary__pill--tackentry">
                {props.tack}
              </span>
            </div>
          </div>
        </Show>
        <Show when={props.tackEntry === "PORT" || props.tackEntry === "STBD"}>
          <div class="performance-filter-summary__group">
            <span class="performance-filter-summary__row-label">Tack:</span>
            <div class="performance-filter-summary__pills">
              <span class="performance-filter-summary__pill performance-filter-summary__pill--tackentry">
                {props.tackEntry === "PORT" ? "PORT ENTRY" : "STBD ENTRY"}
              </span>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}
